/**
 * Generic action-approval hooks (Automations cross-object actions, design 2026-06-30 §5.3).
 *
 * This is the type-agnostic generalization of the email-only `useEmailApprovals`
 * (`features/activities`): it reads the same `agent-approvals` sign-off queue but surfaces
 * EVERY pending action type — `email.send` plus the cross-object `event/task/note/contact.
 * {create,update,delete}` the reactive responder now proposes — flattened into a render-ready
 * `ActionApproval`.
 *
 * It lives in `common/` (not `features/`) on purpose: the layering rule forbids `features/`
 * from importing `modules/`, so shared approval logic that BOTH the Activity Center
 * (`features/activities`) and the Automations run timeline (`modules/automations`) can reuse
 * has to live here. The hooks are pure of session — `token` / `workspaceId` are passed in by
 * the caller (which resolves them from `useSession`) so `common/` never reaches up into
 * `features/auth`.
 *
 * `useDecideActionApproval` only RECORDS the decision server-side; execution-on-approve is a
 * separately-gated step (the executor), mirroring the agent-trust contract.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import * as api from '@/lib/api/endpoints/agent-trust';
import type { AgentApproval } from '@/lib/api/endpoints/agent-trust';
import {
  actionObjectType,
  actionOperation,
  type ActionOperation,
} from '@/common/utils/action-vocabulary';

/** A pending action approval, flattened for a generic review card. */
export interface ActionApproval {
  id: string;
  agentId: string | null;
  actionType: string;
  /** Coarse operation for card rendering: 'send' | 'create' | 'update' | 'delete' | 'other'. */
  operation: ActionOperation;
  /** The cross-object type this concerns ('event' | 'task' | 'note' | 'contact'), or null. */
  objectType: string | null;
  /**
   * For an edit/delete: the SERVER-RESOLVED concrete target object id (from
   * `preparedPayload.targetObjectId`, also mirrored in `provenance.read`). This is the
   * load-bearing value the concrete-target confirmation renders — the human approves THAT
   * specific object, and D7 keeps it server-owned so an edited approval can't retarget it.
   */
  targetObjectId?: string;
  /**
   * For an edit/delete: a human-readable label for the SERVER-RESOLVED target (from
   * `preparedPayload.targetLabel`), derived from the user's own workspace object — e.g.
   * "Board meeting — Tue 10:00". Display-only; the concrete-target confirmation renders THIS as
   * the primary line and falls back to `targetObjectId` when it is absent. Never sent/executed.
   */
  targetLabel?: string;
  /** The full prepared payload, preserved so an edit can patch just the editable fields. */
  preparedPayload: Record<string, unknown>;
  provenance: Record<string, unknown>;
  noveltyFlags: string[];
  /** Private note from Xavior about the draft — a sibling of `preparedPayload`, never executed. */
  notes?: string;
  /** One-line summary of the incoming content the action responds to — user-only, never executed. */
  incomingSummary?: string;
  /** Convenience email view, present only when `operation === 'send'`. */
  email?: { to: string; subject: string; body: string };
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Flatten one raw `AgentApproval` into the render-ready `ActionApproval`. Pure — exported so
 *  `features/activities` (email narrowing) and tests can reuse it without the query. */
export function toActionApproval(a: AgentApproval): ActionApproval {
  const payload = a.preparedPayload ?? {};
  const operation = actionOperation(a.actionType);
  const target = typeof payload.targetObjectId === 'string' ? payload.targetObjectId : undefined;
  const targetLabel =
    typeof payload.targetLabel === 'string' && payload.targetLabel.trim()
      ? payload.targetLabel
      : undefined;
  const email =
    operation === 'send'
      ? {
          to: str(payload.to) || str(payload.recipient),
          subject: str(payload.subject),
          body: str(payload.body) || str(payload.text),
        }
      : undefined;
  return {
    id: a.id,
    agentId: a.agentId,
    actionType: a.actionType,
    operation,
    objectType: actionObjectType(a.actionType),
    targetObjectId: target,
    targetLabel,
    preparedPayload: payload,
    provenance: a.provenance ?? {},
    noveltyFlags: a.noveltyFlags ?? [],
    notes: typeof a.notes === 'string' && a.notes.trim() ? a.notes : undefined,
    incomingSummary:
      typeof a.incomingSummary === 'string' && a.incomingSummary.trim()
        ? a.incomingSummary
        : undefined,
    email,
  };
}

/**
 * The pending action approvals for a workspace. Polls every ~15s so approvals from background
 * runs surface without a manual refresh (mirrors the email hook's cadence).
 *
 * `options.enabled` (default true) lets an always-mounted observer gate the poll so it isn't
 * running idle: the revise-session reconciler passes `enabled: reviseActive` so it only observes
 * (and polls) while a session is live — outside one it adds no background request. The query KEY is
 * unchanged, so a gated observer still dedupes with the Activity Center's ungated one when both are
 * mounted (during a revise session, when notifications are open).
 */
export function useActionApprovals(
  token: string | null | undefined,
  workspaceId: string,
  options?: { enabled?: boolean },
) {
  const q = useQuery({
    queryKey: queryKeys.agentTrust.approvals(workspaceId),
    queryFn: () => api.listApprovals(token!, workspaceId),
    enabled: (options?.enabled ?? true) && !!token && !!workspaceId,
    refetchInterval: 15_000,
  });

  const items = (q.data?.items ?? [])
    .filter((a) => a.status === 'pending')
    .map(toActionApproval);

  return { ...q, items };
}

/**
 * Record a Revise-with-AI payload rewrite from the web side. Keeps the approval PENDING (the server
 * never transitions state); on success it invalidates the approvals query + activities feed so the
 * Review card reflects the new body.
 */
export function useUpdateApproval(token: string | null | undefined, workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: string }) =>
      api.updateApproval(token!, workspaceId, vars.id, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.agentTrust.approvals(workspaceId) });
      void qc.invalidateQueries({ queryKey: queryKeys.activities.all(workspaceId) });
    },
  });
}

export interface DecideActionApprovalVars {
  id: string;
  decision: 'approved' | 'edited' | 'rejected';
  preparedPayload?: Record<string, unknown>;
}

/**
 * Record a decision on an action approval (approve / save-edit / reject). On success it
 * invalidates the approvals query and the activities feed so the Review tab and its count both
 * settle. Execution-on-approve is gated separately.
 */
export function useDecideActionApproval(token: string | null | undefined, workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: DecideActionApprovalVars) =>
      api.decideApproval(token!, workspaceId, vars.id, vars.decision, vars.preparedPayload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.agentTrust.approvals(workspaceId) });
      void qc.invalidateQueries({ queryKey: queryKeys.activities.all(workspaceId) });
    },
  });
}
